// Dependency graph of Qlik master items and variables, for decomposing an
// expression into every level it references (variables, calculated and
// parameterized variables, master measures) and for "used by" lookups.
//
// Reference rules mirror the worker's analyzer (`apps/worker/internal/qlik/
// expression_parser.go`, `scanExpression` in `analyzer.go`): `$(var)` is read
// from the raw text (it expands even inside string literals), `$(=expr)` is
// scanned for nested references, master measures are matched by title as
// `[Title]` / `"Title"` ignoring case, and single-quoted literals register no
// measure or bare-variable reference. Two deliberate differences: variable
// names match with exact case (Qlik is case-sensitive; the analyzer lowercases)
// and a bare one-word measure title is not a reference here.

export type GraphItemKind = "measure" | "dimension" | "variable";
export type GraphSourceId = "expression" | "label" | `field:${number}`;

export interface GraphItemInput {
  kind: GraphItemKind;
  id: string;
  /** Measure/dimension title or variable name. */
  name: string;
  /** A variable's definition and a measure's expression go in "expression". */
  sources: { id: GraphSourceId; text: string }[];
}

export interface ExpressionGraphOptions {
  /**
   * Kind given to a `$(name)` that matches no variable. Sync groups pass
   * "outside": the graph holds only the group's items, so an unknown name is
   * one the group does not manage, not one that does not exist.
   */
  unresolved?: "missing" | "outside";
}

export type DecompositionNodeKind =
  | "measure"
  | "variable"
  | "calc"
  | "param"
  | "outside"
  | "missing";

export interface DecompositionRow {
  /** 0 = referenced directly by the decomposed source. */
  level: number;
  parentIndex: number | null;
  kind: DecompositionNodeKind;
  name: string;
  /** null for outside/missing references. */
  itemId: string | null;
  /** Variable definition or measure expression; "" for outside/missing. */
  definition: string;
  /** Arguments of a parameterized call, `$(t('key'))` → ["'key'"]. */
  args: string[];
  /** Referenced by name, without `$()`. */
  bare: boolean;
  /** Already detailed earlier in this tree; its children are not repeated. */
  repeated: boolean;
  /** Points back to an ancestor; not expanded. */
  cycle: boolean;
  childCount: number;
  /** How many items reference this one directly, across the graph. */
  usedByCount: number;
}

export type FlatSegmentKind = "own" | "variable" | "calc" | "measure" | "outside" | "missing";

export interface FlatSegment {
  text: string;
  kind: FlatSegmentKind;
  /** First-level variable (or measure) the text came from; null for the item's own text. */
  origin: string | null;
}

export interface Decomposition {
  rows: DecompositionRow[];
  flat: FlatSegment[];
  levels: number;
  uniqueItems: number;
  truncated: boolean;
}

export interface GraphRef {
  itemId: string;
  kind: GraphItemKind;
  name: string;
}

export interface UsedBy {
  direct: GraphRef[];
  /** `via` = the item this one uses on its way to the target. */
  indirect: (GraphRef & { via: string })[];
}

// ponytail: fixed ceilings against runaway expansion; make them options only if a real app hits them.
const MAX_DEPTH = 30;
const MAX_FLAT_CHARS = 50_000;

export interface ParsedRef {
  pos: number;
  type: "variable" | "measure";
  name: string;
  args: string[];
  bare: boolean;
}

export interface GraphItem extends GraphItemInput {
  refs: Map<GraphSourceId, ParsedRef[]>;
}

export interface ExpressionGraph {
  items: Map<string, GraphItem>;
  variablesByName: Map<string, GraphItem>;
  measuresByTitle: Map<string, GraphItem>;
  usersOf: Map<string, Set<string>>;
  unresolved: "missing" | "outside";
}

const IDENTIFIER = /(?<![\p{L}\p{N}_.%$#])[\p{L}_][\p{L}\p{N}_]*(?![\p{L}\p{N}_.])/gu;

/** Calls visit(start, end, content) for each balanced top-level `$(...)`. Stops at the first unbalanced one. */
function scanDollarExpansions(
  s: string,
  visit: (start: number, end: number, content: string) => void,
) {
  for (let i = 0; i + 1 < s.length; i++) {
    if (s[i] !== "$" || s[i + 1] !== "(") continue;
    let depth = 1;
    let j = i + 2;
    for (; j < s.length; j++) {
      if (s[j] === "(") depth++;
      else if (s[j] === ")" && --depth === 0) break;
    }
    if (depth !== 0) return;
    visit(i, j + 1, s.slice(i + 2, j));
    i = j;
  }
}

function blank(s: string, start: number, end: number): string {
  return s.slice(0, start) + " ".repeat(end - start) + s.slice(end);
}

/** Blanks single-quoted literal content ('' escapes included); bracketed names stay verbatim. */
function stripStringLiterals(s: string): string {
  let out = "";
  let inBracket = false;
  let inString = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (inString) {
      if (c === "'" && s[i + 1] === "'") {
        out += "  ";
        i++;
      } else if (c === "'") {
        inString = false;
        out += c;
      } else {
        out += " ";
      }
      continue;
    }
    if (inBracket) {
      if (c === "]") inBracket = false;
    } else if (c === "[") {
      inBracket = true;
    } else if (c === "'") {
      inString = true;
    }
    out += c;
  }
  return out;
}

/** Splits call arguments on top-level commas, respecting quotes, brackets and parentheses. */
function splitArgs(s: string): string[] {
  const args: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let cur = "";
  for (const c of s) {
    if (quote) {
      if (c === quote) quote = null;
    } else if (c === "'" || c === '"') {
      quote = c;
    } else if (c === "[") {
      quote = "]";
    } else if (c === "(") {
      depth++;
    } else if (c === ")") {
      depth--;
    } else if (c === "," && depth === 0) {
      args.push(cur.trim());
      cur = "";
      continue;
    }
    cur += c;
  }
  args.push(cur.trim());
  return args;
}

/** `$(name)` / `$(#name)` / `$(name(args))` → name and raw argument text. */
function parseCall(content: string): { name: string; argsText: string | null } {
  const p = content.indexOf("(");
  const head = (p >= 0 ? content.slice(0, p) : content).trim().replace(/^#/, "");
  if (p < 0) return { name: head, argsText: null };
  const close = content.lastIndexOf(")");
  return { name: head, argsText: content.slice(p + 1, close > p ? close : content.length) };
}

function parseRefs(
  text: string,
  self: { measureTitle?: string; variableName?: string },
  variableNames: Set<string>,
  measureTitles: Set<string>,
  offset = 0,
): ParsedRef[] {
  const refs: ParsedRef[] = [];
  let masked = text;

  scanDollarExpansions(text, (start, end, content) => {
    masked = blank(masked, start, end);
    const inner = offset + start + 2;
    if (content.startsWith("=")) {
      refs.push(...parseRefs(content.slice(1), self, variableNames, measureTitles, inner + 1));
      return;
    }
    const { name, argsText } = parseCall(content);
    // `$($1)` inside a parameterized definition is a placeholder, not a variable.
    if (!name || /^\$?\d+$/.test(name)) return;
    refs.push({
      pos: offset + start,
      type: "variable",
      name,
      args: argsText === null ? [] : splitArgs(argsText),
      bare: false,
    });
    // Arguments are read where the call is made: `$(firstField(fFlag))` uses fFlag.
    if (argsText !== null) {
      const argsAt = inner + content.indexOf("(") + 1;
      refs.push(...parseRefs(argsText, self, variableNames, measureTitles, argsAt));
    }
  });

  const scan = stripStringLiterals(masked);
  for (const m of scan.matchAll(/\[([^\]]+)\]|"([^"]+)"/g)) {
    const title = (m[1] ?? m[2])!;
    const lower = title.toLowerCase();
    if (measureTitles.has(lower) && lower !== self.measureTitle) {
      refs.push({ pos: offset + m.index, type: "measure", name: title, args: [], bare: false });
    }
  }

  const bareScan = scan.replace(/\[[^\]]*\]/g, (b) => " ".repeat(b.length));
  for (const m of bareScan.matchAll(IDENTIFIER)) {
    const name = m[0];
    if (!variableNames.has(name) || name === self.variableName) continue;
    const after = bareScan.slice(m.index + name.length).trimStart();
    if (after.startsWith("(")) continue;
    refs.push({ pos: offset + m.index, type: "variable", name, args: [], bare: true });
  }

  return refs.sort((a, b) => a.pos - b.pos);
}

function selfOf(item: GraphItemInput) {
  return item.kind === "measure"
    ? { measureTitle: item.name.toLowerCase() }
    : item.kind === "variable"
      ? { variableName: item.name }
      : {};
}

/** A dimension field def is a field name unless written as an expression (`=...`). */
function isPlainField(item: GraphItemInput, id: GraphSourceId, text: string): boolean {
  return item.kind === "dimension" && id.startsWith("field:") && !text.trimStart().startsWith("=");
}

/** Text scanned for references: a plain field def keeps only its `$()` expansions. */
function sourceText(item: GraphItemInput, id: GraphSourceId, text: string): string {
  if (!isPlainField(item, id, text)) return text;
  let out = " ".repeat(text.length);
  scanDollarExpansions(text, (start, end) => {
    out = out.slice(0, start) + text.slice(start, end) + out.slice(end);
  });
  return out;
}

export function buildExpressionGraph(
  inputs: GraphItemInput[],
  opts: ExpressionGraphOptions = {},
): ExpressionGraph {
  const items = new Map<string, GraphItem>();
  const variablesByName = new Map<string, GraphItem>();
  const measuresByTitle = new Map<string, GraphItem>();
  for (const input of inputs) {
    const item: GraphItem = { ...input, refs: new Map() };
    items.set(item.id, item);
    if (item.kind === "variable" && !variablesByName.has(item.name)) {
      variablesByName.set(item.name, item);
    }
    const title = item.name.toLowerCase();
    if (item.kind === "measure" && !measuresByTitle.has(title)) measuresByTitle.set(title, item);
  }

  const variableNames = new Set(variablesByName.keys());
  const measureTitles = new Set(measuresByTitle.keys());
  const graph: ExpressionGraph = {
    items,
    variablesByName,
    measuresByTitle,
    usersOf: new Map(),
    unresolved: opts.unresolved ?? "missing",
  };

  for (const item of items.values()) {
    for (const source of item.sources) {
      const text = sourceText(item, source.id, source.text);
      const refs = parseRefs(text, selfOf(item), variableNames, measureTitles);
      item.refs.set(source.id, refs);
      for (const ref of refs) {
        const target = resolve(graph, ref);
        if (!target || target.id === item.id) continue;
        let users = graph.usersOf.get(target.id);
        if (!users) graph.usersOf.set(target.id, (users = new Set()));
        users.add(item.id);
      }
    }
  }
  return graph;
}

function resolve(graph: ExpressionGraph, ref: ParsedRef): GraphItem | undefined {
  return ref.type === "variable"
    ? graph.variablesByName.get(ref.name)
    : graph.measuresByTitle.get(ref.name.toLowerCase());
}

/** The text a referenced item contributes: a variable's definition or a measure's expression. */
function bodyOf(item: GraphItem): { id: GraphSourceId; text: string } | undefined {
  return item.sources.find((s) => s.id === "expression");
}

function variableKind(definition: string): DecompositionNodeKind {
  if (definition.trimStart().startsWith("=")) return "calc";
  if (/\$\d/.test(definition)) return "param";
  return "variable";
}

export function decompose(
  graph: ExpressionGraph,
  itemId: string,
  sourceId: GraphSourceId,
): Decomposition {
  const root = graph.items.get(itemId);
  const empty: Decomposition = { rows: [], flat: [], levels: 0, uniqueItems: 0, truncated: false };
  const source = root?.sources.find((s) => s.id === sourceId);
  if (!root || !source) return empty;

  const rows: DecompositionRow[] = [];
  const seen = new Set<string>();
  let truncated = false;

  const walk = (
    refs: ParsedRef[],
    level: number,
    parentIndex: number | null,
    stack: Set<string>,
  ) => {
    const keys = new Set<string>();
    for (const ref of refs) {
      const key = `${ref.type}:${ref.name}:${ref.args.join(",")}`;
      if (keys.has(key)) continue;
      keys.add(key);
      if (parentIndex !== null) rows[parentIndex]!.childCount++;

      const target = resolve(graph, ref);
      if (!target) {
        rows.push(row(ref, level, parentIndex, graph.unresolved, null, ""));
        continue;
      }
      const body = bodyOf(target)?.text ?? "";
      const kind = target.kind === "variable" ? variableKind(body) : "measure";
      const index = rows.push(row(ref, level, parentIndex, kind, target.id, body)) - 1;
      const current = rows[index]!;
      current.usedByCount = graph.usersOf.get(target.id)?.size ?? 0;
      if (stack.has(target.id)) {
        current.cycle = true;
        continue;
      }
      if (seen.has(key)) {
        current.repeated = true;
        continue;
      }
      seen.add(key);
      if (level + 1 >= MAX_DEPTH) {
        truncated = true;
        continue;
      }
      walk(target.refs.get("expression") ?? [], level + 1, index, new Set(stack).add(target.id));
    }
  };

  walk(root.refs.get(sourceId) ?? [], 0, null, new Set([root.id]));
  const flat = flatten(graph, root, source.text, !isPlainField(root, sourceId, source.text));

  return {
    rows,
    flat: flat.segments,
    levels: rows.reduce((max, r) => Math.max(max, r.level + 1), 0),
    uniqueItems: rows.filter((r) => !r.repeated && !r.cycle).length,
    truncated: truncated || flat.truncated,
  };
}

function row(
  ref: ParsedRef,
  level: number,
  parentIndex: number | null,
  kind: DecompositionNodeKind,
  itemId: string | null,
  definition: string,
): DecompositionRow {
  return {
    level,
    parentIndex,
    kind,
    name: ref.name,
    itemId,
    definition,
    args: ref.args,
    bare: ref.bare,
    repeated: false,
    cycle: false,
    childCount: 0,
    usedByCount: 0,
  };
}

/**
 * Text substitution as Qlik's `$()` does it: a variable becomes its definition
 * (`$n` replaced by the call's arguments), a calculated variable becomes
 * `$(=...)` because Qlik uses its result, and master measures and bare names
 * stay as written since Qlik resolves those by name.
 */
function flatten(graph: ExpressionGraph, root: GraphItem, text: string, matchMeasures: boolean) {
  const segments: FlatSegment[] = [];
  let length = 0;
  let truncated = false;
  const measureTitles = new Set(matchMeasures ? graph.measuresByTitle.keys() : []);
  const rootTitle = root.kind === "measure" ? root.name.toLowerCase() : undefined;

  const emit = (t: string, kind: FlatSegmentKind, origin: string | null) => {
    if (!t || truncated) return;
    if (length + t.length > MAX_FLAT_CHARS) {
      truncated = true;
      t = t.slice(0, MAX_FLAT_CHARS - length);
    }
    length += t.length;
    const last = segments[segments.length - 1];
    if (last && last.kind === kind && last.origin === origin) last.text += t;
    else segments.push({ text: t, kind, origin });
  };

  const emitPlain = (t: string, kind: FlatSegmentKind, origin: string | null) => {
    const scan = stripStringLiterals(t);
    let last = 0;
    for (const m of scan.matchAll(/\[([^\]]+)\]|"([^"]+)"/g)) {
      const lower = (m[1] ?? m[2])!.toLowerCase();
      if (!measureTitles.has(lower) || lower === rootTitle) continue;
      emit(t.slice(last, m.index), kind, origin);
      emit(t.slice(m.index, m.index + m[0].length), "measure", m[1] ?? m[2]!);
      last = m.index + m[0].length;
    }
    emit(t.slice(last), kind, origin);
  };

  const expand = (t: string, kind: FlatSegmentKind, origin: string | null, stack: Set<string>) => {
    if (stack.size > MAX_DEPTH) {
      truncated = true;
      return;
    }
    let last = 0;
    scanDollarExpansions(t, (start, end, content) => {
      emitPlain(t.slice(last, start), kind, origin);
      last = end;
      const raw = t.slice(start, end);
      if (content.startsWith("=")) {
        emit("$(=", "calc", origin);
        expand(content.slice(1), "calc", origin, stack);
        emit(")", "calc", origin);
        return;
      }
      const { name, argsText } = parseCall(content);
      const target = graph.variablesByName.get(name);
      if (!target || stack.has(target.id)) {
        emit(raw, target ? kind : graph.unresolved, origin ?? name);
        return;
      }
      const args = argsText === null ? [] : splitArgs(argsText);
      const body = (bodyOf(target)?.text ?? "").replace(
        /\$(\d+)/g,
        (m, n: string) => args[Number(n) - 1] ?? m,
      );
      const from = origin ?? name;
      const next = new Set(stack).add(target.id);
      if (body.trimStart().startsWith("=")) {
        emit("$(=", "calc", from);
        expand(body.trimStart().slice(1), "calc", from, next);
        emit(")", "calc", from);
      } else {
        expand(body, kind === "calc" ? "calc" : "variable", from, next);
      }
    });
    emitPlain(t.slice(last), kind, origin);
  };

  expand(text, "own", null, new Set([root.id]));
  return { segments, truncated };
}

export function usedBy(graph: ExpressionGraph, itemId: string): UsedBy {
  const toRef = (id: string): GraphRef => {
    const item = graph.items.get(id)!;
    return { itemId: id, kind: item.kind, name: item.name };
  };
  const byName = (a: GraphRef, b: GraphRef) => a.name.localeCompare(b.name);

  const directIds = [...(graph.usersOf.get(itemId) ?? [])];
  const seen = new Set([itemId, ...directIds]);
  const indirect: (GraphRef & { via: string })[] = [];
  const queue = directIds.map((id) => ({ id, name: graph.items.get(id)!.name }));
  while (queue.length) {
    const { id, name } = queue.shift()!;
    for (const userId of graph.usersOf.get(id) ?? []) {
      if (seen.has(userId)) continue;
      seen.add(userId);
      indirect.push({ ...toRef(userId), via: name });
      queue.push({ id: userId, name: graph.items.get(userId)!.name });
    }
  }
  return { direct: directIds.map(toRef).sort(byName), indirect };
}
